import React from "react";
import { MenuItem, Select, Stack, TextField, Typography } from "@mui/material";
import { useFormikContext } from "formik";

import FieldGroup from "../../../../shared/FieldGroup";
import SwitchSettingBar from "../../elements/SwitchSettingBar";
import type {
  MasteryPromptLevel,
  TargetFormValues,
  DiscreteTrialProcedure as DiscreteTrialProcedureValues,
} from "../../TargetBuilderPanel.types";
import {
  FIRST_TRIAL_TYPE_OPTIONS,
  MASTERY_PROMPT_LEVEL_OPTIONS,
  isDiscreteTrialProcedure,
} from "../../TargetBuilderPanel.types";
import { usePromptHierarchy } from "../../hooks/usePromptHierarchy";
import PromptHierarchyEditor from "./PromptHierarchyEditor";
import TeachingProcedureCommonFields from "./TeachingProcedureCommonFields";

interface DiscreteTrialProcedureProps {
  /** When true, hide Prompt Hierarchy, Mastery Prompt Level, and Count Prompted as Correct. */
  hidePromptHierarchy?: boolean;
  /** When true, hide First Trial Type (original rules without behavior-reduction). */
  hideFirstTrialType?: boolean;
  /** When true, hide Teaching Opportunities per Session (caregiver goals use Practice Opportunities instead). */
  hideTeachingOpportunities?: boolean;
  /** When true, mark L3-specific fields as optional (caregiver mode). */
  fieldsOptional?: boolean;
}

const DiscreteTrialProcedure: React.FC<DiscreteTrialProcedureProps> = ({
  hidePromptHierarchy,
  hideFirstTrialType,
  hideTeachingOpportunities,
  fieldsOptional,
}) => {
  const { values, setFieldValue } = useFormikContext<TargetFormValues>();
  if (!isDiscreteTrialProcedure(values.teaching_procedure)) return null;

  const procedure: DiscreteTrialProcedureValues = values.teaching_procedure;
  const update = (partial: Partial<DiscreteTrialProcedureValues>) =>
    setFieldValue("teaching_procedure", { ...procedure, ...partial });

  return (
    <Stack gap={6}>
      <TeachingProcedureCommonFields />

      <FieldGroup
        label="Discriminative Stimulus (SD)"
        description="What cue or instruction signals the learner to respond?"
        required={!fieldsOptional}
      >
        <TextField
          multiline
          minRows={2}
          fullWidth
          value={procedure.sd_wording}
          onChange={(e) => update({ sd_wording: e.target.value })}
          placeholder="e.g., 'Touch [item]' or 'What is this?'"
        />
      </FieldGroup>
      <FieldGroup
        label="Correct Response"
        description="What constitutes a correct response to the SD?"
        required={!fieldsOptional}
      >
        <TextField
          multiline
          minRows={2}
          fullWidth
          value={procedure.correct_response}
          onChange={(e) => update({ correct_response: e.target.value })}
          placeholder="e.g., 'Learner touches the correct item within 3 seconds'"
        />
      </FieldGroup>

      {!hidePromptHierarchy && <PromptHierarchyEditor />}

      <FieldGroup
        label="Error Correction Procedure"
        description="What happens when the learner makes an error?"
        required={!fieldsOptional}
      >
        <TextField
          multiline
          minRows={2}
          fullWidth
          value={procedure.error_correction}
          onChange={(e) => update({ error_correction: e.target.value })}
          placeholder="e.g., 'Model correct response, re-present SD, provide least-to-most prompts'"
        />
      </FieldGroup>

      {!hideTeachingOpportunities && (
        <FieldGroup
          label="Teaching Opportunities per Session"
          description="What is the minimum teaching opportunities you plan for each session?"
          required={!fieldsOptional}
        >
          <TextField
            size="small"
            type="number"
            value={procedure.teaching_opportunities_per_session ?? ""}
            onChange={(e) => update({ teaching_opportunities_per_session: e.target.value })}
            placeholder="e.g., 1"
            aria-label="Teaching opportunities per session"
            inputProps={{ min: 1 }}
            InputProps={{ sx: { width: 120 } }}
          />
        </FieldGroup>
      )}

      {!hideFirstTrialType && (
        <FieldGroup
          label="First Trial Type"
          description="How should the first trial of each session be conducted?"
        >
          <Select
            size="small"
            fullWidth
            value={procedure.first_trial_type}
            onChange={(e) =>
              update({
                first_trial_type: e.target
                  .value as DiscreteTrialProcedureValues["first_trial_type"],
              })
            }
            aria-label="First trial type"
            displayEmpty
            renderValue={(selected) =>
              selected ? (
                FIRST_TRIAL_TYPE_OPTIONS.find((o) => o.value === selected)?.label
              ) : (
                <Typography variant="body2" color="text.disabled">
                  Select first trial type
                </Typography>
              )
            }
          >
            {FIRST_TRIAL_TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FieldGroup>
      )}

      {!hidePromptHierarchy && <CountPromptedSection />}
    </Stack>
  );
};

/**
 * Internal helper: Count Prompted as Correct toggle + conditional Mastery Prompt Level.
 * Uses the shared prompt hierarchy hook to read/write from `prompt_hierarchy`.
 */
const CountPromptedSection: React.FC = () => {
  const { hierarchy, hasHierarchy, setCountPromptedAsCorrect, setMasteryPromptLevel } =
    usePromptHierarchy();

  if (!hasHierarchy || !hierarchy) return null;

  return (
    <Stack gap={4}>
      <SwitchSettingBar
        title="Count Prompted as Correct"
        description="Include prompted trials in % correct calculation"
        checked={hierarchy.count_prompted_as_correct}
        onChange={setCountPromptedAsCorrect}
      />

      {hierarchy.count_prompted_as_correct && (
        <FieldGroup
          label="Mastery Prompt Level"
          description="What prompt level counts toward mastery criteria?"
        >
          <Select
            size="small"
            fullWidth
            value={hierarchy.mastery_prompt_level}
            onChange={(e) => setMasteryPromptLevel(e.target.value as MasteryPromptLevel | "")}
            aria-label="Mastery prompt level"
            displayEmpty
            renderValue={(selected) =>
              selected ? (
                MASTERY_PROMPT_LEVEL_OPTIONS.find((o) => o.value === selected)?.label
              ) : (
                <Typography variant="body2" color="text.disabled">
                  Select mastery prompt level
                </Typography>
              )
            }
          >
            {MASTERY_PROMPT_LEVEL_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FieldGroup>
      )}
    </Stack>
  );
};

export default DiscreteTrialProcedure;
